import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';
import pairCigarAssignmentsApi from '../../../api/pairCigarAssignmentsApi';

// Types
interface PlanActualParams {
  plans: Array<{
    planId: string;
    cigarId: string;
    start: Dayjs;
    end: Dayjs;
  }>;
}

interface PlanActualTotals {
  [planId: string]: number;
}

interface PairCigarAssignmentsState {
  planActuals: {
    totals: PlanActualTotals;
    totalsV2: PlanActualTotals;
    taskTotalsV2: PlanActualTotals;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
    fetchKey: string | null;
  };
}

// Initial State
const initialState: PairCigarAssignmentsState = {
  planActuals: {
    totals: {},
    totalsV2: {},
    taskTotalsV2: {},
    loading: 'idle',
    error: null,
    fetchKey: null,
  },
};

// Thunks
export const fetchPlanActuals = createAsyncThunk<
  { totals: PlanActualTotals; totalsV2: PlanActualTotals; taskTotalsV2: PlanActualTotals; fetchKey: string },
  PlanActualParams,
  { rejectValue: string }
>('pairCigarAssignments/fetchPlanActuals', async ({ plans }, thunkAPI) => {
  try {
    if (plans.length === 0) {
      return { totals: {}, totalsV2: {}, taskTotalsV2: {}, fetchKey: '' };
    }

    const planIds = plans.map((p) => p.planId);
    const fetchKey = plans
      .map(
        (entry) =>
          `${entry.planId}:${entry.cigarId}:${entry.start.format('YYYY-MM-DD')}:${entry.end.format('YYYY-MM-DD')}`
      )
      .sort()
      .join('|');

    const overallStart = plans.reduce((min, entry) => (entry.start.isBefore(min) ? entry.start : min), plans[0].start);
    const overallEnd = plans.reduce((max, entry) => (entry.end.isAfter(max) ? entry.end : max), plans[0].end);

    // Call BOTH APIs in parallel
    const [oldRes, newRes] = await Promise.all([
      pairCigarAssignmentsApi.getActualProduction({
        startDate: overallStart.format('YYYY-MM-DD'),
        endDate: overallEnd.format('YYYY-MM-DD'),
      }),
      pairCigarAssignmentsApi.getPlanProductionActuals({
        planIds,
      }),
    ]);

    // Process OLD logic
    const oldTotals: Record<string, number> = {};
    if (oldRes && oldRes.status < 400) {
      const oldData = oldRes.data?.data || {};
      const cigarTotals = (oldData?.cigarTotals as Record<string, Record<string, number>>) || {};

      plans.forEach(({ planId, cigarId, start, end }) => {
        const totals = cigarTotals[cigarId] || {};
        let actual = 0;

        Object.entries(totals).forEach(([dayKey, value]) => {
          const day = dayjs(dayKey);
          if (day.isValid() && !day.isBefore(start, 'day') && !day.isAfter(end, 'day')) {
            actual += Number(value) || 0;
          }
        });
        oldTotals[planId] = actual;
      });
    }

    // Process NEW logic
    const newTotals: Record<string, number> = {};
    const newTaskTotals: Record<string, number> = {};
    if (newRes && newRes.status < 400) {
      const newData = newRes.data?.data || {};
      Object.assign(newTotals, newData.planTotals || {});
      Object.assign(newTaskTotals, newData.taskTotals || {});
    }

    return { totals: oldTotals, totalsV2: newTotals, taskTotalsV2: newTaskTotals, fetchKey };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message || 'Failed to load plan actuals');
  }
});

// Slice
export const pairCigarAssignmentsSlice = createSlice({
  name: 'pairCigarAssignments',
  initialState,
  reducers: {
    clearPlanActuals: (state) => {
      state.planActuals = initialState.planActuals;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlanActuals.pending, (state) => {
      state.planActuals.loading = 'pending';
      state.planActuals.error = null;
    });

    builder.addCase(fetchPlanActuals.fulfilled, (state, action) => {
      state.planActuals.loading = 'succeeded';
      state.planActuals.totals = action.payload.totals;
      state.planActuals.totalsV2 = action.payload.totalsV2;
      state.planActuals.taskTotalsV2 = action.payload.taskTotalsV2;
      state.planActuals.fetchKey = action.payload.fetchKey;
      state.planActuals.error = null;
    });

    builder.addCase(fetchPlanActuals.rejected, (state, action) => {
      state.planActuals.loading = 'failed';
      state.planActuals.error = action.payload || 'Unknown error';
      state.planActuals.totals = {};
      state.planActuals.totalsV2 = {};
      state.planActuals.taskTotalsV2 = {};
    });
  },
});

export const { clearPlanActuals } = pairCigarAssignmentsSlice.actions;

export default pairCigarAssignmentsSlice.reducer;

